import { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { get, includes, isEmpty, isEqual, pick } from "lodash"
import { DialogContent, DialogActions, Button, Switch, Grid, Typography, FormControl, InputAdornment, TextField } from "@mui/material"
import UnifiedSearch from "modules/unified-search"
import { Info as InfoIcon, Search as SearchIcon } from "@mui/icons-material"
import { getInstrumentPortfolioGMV } from "modules/research-topic/selectors/items"
import { dollarFormatter, NEGATIVE_FORMATS } from "helpers/formatters/numbers"
import OPTooltip from "components/material/tooltip"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"

import "./style.scss"

class HedgeOptions extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    modelId: PropTypes.string.isRequired,
    comparisonItem: PropTypes.object.isRequired,
    currentHedges: PropTypes.array.isRequired,
    closeDialog: PropTypes.func.isRequired,
    addHedge: PropTypes.func.isRequired,
    editHedge: PropTypes.func.isRequired,
    removeHedge: PropTypes.func.isRequired,
    addQuickHedge: PropTypes.func.isRequired,
    quickHedge: PropTypes.bool,
  }

  constructor(props) {
    super()
    const { comparisonItem, quickHedge } = props
    const { hedge } = !quickHedge ? comparisonItem : {}

    this.state = {
      useHedge: true,
      instrument: get(hedge, "instrument", null),
      size: get(hedge, "size", -100),
      isSearchOpen: false,
      focus: false,
    }
  }

  searchItemsActions = () => ({
    index: {
      noDispatch: true,
      mainAction: item => () => {
        this.setSelectedSearchItem(item)
      },
    },
    security: {
      noDispatch: true,
      mainAction: item => () => {
        this.setSelectedSearchItem(item)
      },
    },
  })

  setSelectedSearchItem = (item) => {
    this.setState({
      instrument: item,
      isSearchOpen: false,
      focus: true,
    })
  }

  showInstrumentSearch = () => {
    this.setState({
      isSearchOpen: true,
      instrument: null,
    })
  }

  disableSearchResult = (searchResult) => {
    const { comparisonItem } = this.props
    if (!includes(["security", "index"], searchResult.type)) { return true }
    switch (searchResult.assetClass) {
      case "swap":
        return comparisonItem.id === searchResult.ticker
      default:
        return comparisonItem.id === searchResult.id
    }
  }

  sameHedgeOptionValues = () => {
    const { comparisonItem } = this.props
    const { instrument, size } = this.state
    const { hedge } = comparisonItem
    if (!hedge) { return false }
    return isEqual(hedge.instrument, instrument) && hedge.size === size
  }

  validInstrument = () => {
    const { comparisonItem } = this.props
    const { instrument } = this.state
    if (instrument && (comparisonItem.id === instrument.id)) {
      return false
    }
    return true
  }

  validSize = () => {
    const { size } = this.state
    const absSize = Math.abs(size)
    if (absSize !== 0) {
      return true
    }
    return false
  }

  saveEnabled = () => {
    const { useHedge, instrument } = this.state
    if (!useHedge) { return true }
    return !isEmpty(instrument) && this.validInstrument() && this.validSize() && !this.sameHedgeOptionValues()
  }

  handleSaveButton = () => {
    const { comparisonItem, closeDialog, removeHedge } = this.props
    const { useHedge } = this.state
    const { hedge } = comparisonItem
    if (!useHedge && hedge) {
      removeHedge(hedge)
    } else {
      this.saveHedge()
    }
    closeDialog()
  }

  saveHedge = () => {
    const { comparisonItem, addHedge, editHedge, addQuickHedge, quickHedge } = this.props
    const { instrument, size } = this.state
    const id = comparisonItem.duplicate || comparisonItem.id
    const newHedge = {
      id,
      instrument: {
        ...pick(instrument, ["id", "type", "assetClass", "ticker", "internalId"]),
        mic: instrument.mic || instrument.exchange,
      },
      size,
    }

    if (quickHedge) {
      addQuickHedge(comparisonItem, newHedge)
    } else if (comparisonItem.hedge) {
      editHedge(newHedge)
    } else {
      addHedge(newHedge)
    }
  }

  basketHelperText = () => {
    const { gmv, equity } = get(this.props, "comparisonItem.portfolio") || {}
    const portfolioGMV = equity || gmv
    const { size } = this.state
    if (!this.validSize()) { return "Hedge size is invalid" }

    if (portfolioGMV && size) {
      const basket = portfolioGMV * (size / 100)
      return `Portfolio: ${dollarFormatter(portfolioGMV, NEGATIVE_FORMATS.PARENTHESES)}, Basket: ${dollarFormatter(basket, NEGATIVE_FORMATS.PARENTHESES)}`
    }
  }

  getPortfolioName = () => {
    const { comparisonItem } = this.props
    const { baseType } = comparisonItem
    if (baseType !== "portfolio") { return null }
    return comparisonItem.name
  }

  render() {
    const { account, modelId, comparisonItem, closeDialog, quickHedge } = this.props
    const { isSearchOpen, useHedge, instrument, size, focus } = this.state
    return (
      <div className="modules_research-topic-hedge-options">
        {!isSearchOpen && <>
          <DialogContent className="default-hedge">
            {!quickHedge && <Typography className="hedge-toggle" component="div">
              <Grid
                component="label"
                container
                spacing={1}
                sx={{
                alignItems: "center",
              }}
              >
                <Grid>
                  Activate hedging instrument:
                  <OPTooltip
                    classes={{ popper: "hedge-options-popper" }}
                    title={`Adding a hedge will re-evaluate this security, where ${comparisonItem.ticker} will be held long, while using the selected hedging instrument as a short.`}
                  >
                    <InfoIcon />
                  </OPTooltip>
                </Grid>
                <Grid>No</Grid>
                <Grid>
                  <Switch
                    checked={useHedge}
                    onChange={(e) => this.setState({ useHedge: e.target.checked })}
                    name="useHedge"
                    slotProps={{ input: { "aria-label": "secondary checkbox" } }}
                    color="primary"
                  />
                </Grid>
                <Grid>Yes</Grid>
              </Grid>
            </Typography>}
            {this.getPortfolioName()
              && <Typography component="div" className="ref-instrument">
                <FormControl variant="standard" className="field" fullWidth>
                  <TextField
                    variant="standard"
                    disabled
                    id="base-security"
                    name="security"
                    label="Security"
                    value={this.getPortfolioName()}
                    margin="normal"
                  />
                </FormControl>
              </Typography>}
            <Typography component="div">
              <FormControl variant="standard" className="field" fullWidth>
                <TextField
                  variant="standard"
                  disabled={!useHedge}
                  id="hedging-instrument"
                  name="instrument"
                  label="Hedging instrument"
                  value={get(instrument, "name", "")}
                  onFocus={this.showInstrumentSearch}
                  error={!this.validInstrument()}
                  margin="normal"
                  slotProps={{
                    input: {
                      classes: { root: "topic-instrument" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </FormControl>
              <FormControl variant="standard" className="field" fullWidth>
                <TextField
                  variant="standard"
                  disabled={!useHedge}
                  autoFocus={focus}
                  id="hedgeSize"
                  label="Hedge size as a % of reference instrument"
                  type="number"
                  onChange={(e) => this.setState({ size: e.target.value })}
                  value={size}
                  error={!this.validSize()}
                  helperText={this.basketHelperText()}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          %
                        </InputAdornment>
                      ),
                    },

                    htmlInput: {
                      step: 0.1,
                    },
                  }}
                />
              </FormControl>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={() => closeDialog()}>Cancel</Button>
            <Button color="primary" disabled={!this.saveEnabled()} onClick={this.handleSaveButton}>
              Apply
            </Button>
          </DialogActions>
        </>}
        {isSearchOpen
          && <UnifiedSearch
            autoFocus
            skipClose
            account={account}
            modelId={modelId}
            searchType={RECENT_INSTRUMENT_SEARCH_TYPES.GENERAL}
            itemsActions={this.searchItemsActions()}
            emptyContent={<div className="search-empty">
              Search for a hedging instrument
            </div>}
            disableResult={this.disableSearchResult}
            hiddenTypes={["portfolios", "benchmarks", "experiments", "researchTopics", "currencies"]}
          />}
      </div>
    )
  }
}

const mapStateToProps = (state, props) => ({
  portfolioGMV: getInstrumentPortfolioGMV(state, props),
})
export default connect(mapStateToProps)(HedgeOptions)
